import { Done } from '@mui/icons-material';
import { CircularProgress, InputAdornment } from '@mui/material';
import { FC, MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AsyncSearch } from 'components';
import { getAllowOrders, getStopOrders } from 'services/unit-orders';
import { ActiveUnit, AllowOrder, Customer } from 'types';

import { useCustomerAPI } from 'hooks/use-customer-api';
import { useNewTransaction } from '../new-transaction.context';
import useFleetFileUnits from '../use-fleet-file-units';
import WarningAdornment from '../warning-adornment';
import './account.scss';
import InboundAccountSearch from './inbound-account';
import { fleetAccountConflict, fleetCandidatesHint } from './fleet-account-check';

const AllowOrderAdornment: FC<Partial<AllowOrder>> = ({ customer, unitNumber, notes }) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  let timeout = useRef<NodeJS.Timeout>();
  const showPopover = useCallback(() => {
    const popover = popoverRef.current;
    clearTimeout(timeout.current);
    if (popover) {
      popover.hidden = false;
      timeout.current = setTimeout(() => {
        popover.hidden = true;
      }, 2000);
    }
  }, []);

  useEffect(() => {
    if (document.body.isSameNode(document.activeElement)) showPopover();
    return () => clearTimeout(timeout.current);
  }, [showPopover]);

  return (
    <>
      <InputAdornment position="end" onMouseEnter={showPopover} onClick={showPopover}>
        <Done />
      </InputAdornment>
      {!!unitNumber && (
        <div className="success p-3" hidden ref={popoverRef} {...{ popover: true }}>
          {unitNumber} allowed for {customer?.name ?? 'account'}. {notes}
        </div>
      )}
    </>
  );
};

interface AccountSearchProps {
  unit: Partial<ActiveUnit>;
  required?: boolean;
  setCustomer: (customer?: Customer | null) => void;
  ['data-cy']?: string;
}
const LegacyAccountSearch: FC<AccountSearchProps> = ({ unit, required, setCustomer, ...props }) => {
  const { transaction } = useNewTransaction();
  const { searchCustomers } = useCustomerAPI();
  const { getFleetFileUnit } = useFleetFileUnits();
  let inputRef: MutableRefObject<HTMLInputElement | null> = useRef<HTMLInputElement>(null);
  let [adornment, setAdornment] = useState<ReactNode>(null);
  /* the accounts the fleet file allows, when there are several and none of them is selected */
  const [candidatesHint, setCandidatesHint] = useState<string | null>(null);
  /* only the latest check may set the hint; an older lookup landing late must not overwrite it */
  const latestCheck = useRef(0);

  const checkStopOrders = useCallback(
    async (customer?: Customer | null, inputElement?: HTMLInputElement | null) => {
      if (inputElement && !inputElement.isSameNode(inputRef.current)) {
        inputRef.current = inputElement;
      }
      const check = ++latestCheck.current;
      const showHint = (hint: string | null) => {
        if (check === latestCheck.current) setCandidatesHint(hint);
      };
      if (!customer?.id) showHint(null);
      if (customer?.id && unit.unitNumber) {
        setAdornment(<InputAdornment position="end" children={<CircularProgress size={24} />} />);
        /*
         * The fleet check used to start by calling GET /fleet-files/customers/{id}, which is
         * ADMIN/BILLING_ADMIN only. For a gate user that returned 403, the hook turned it into
         * null, and `if (!!fleetFileMapping)` was false — so the check silently did not run for
         * exactly the people doing the gating. The gate-scoped lookup now answers directly.
         */
        const outcome = await getFleetFileUnit(unit.unitNumber, unit.equipmentType ?? undefined);

        /*
         * What the fleet file says is a warning, never a block (decided 2026-09-23): the account the
         * operator chose stays, the note under the field says why, and the server logs the save as an
         * override. Stop and allow orders below still block.
         */
        let fleetWarning: string | null = null;
        switch (outcome.kind) {
          case 'found': {
            /*
             * Membership, not equality: a code may stand for several accounts and any of them is
             * legitimate. A real contradiction warns, naming what the fleet file allows; an
             * unresolved account (no candidates) does not.
             */
            fleetWarning = fleetAccountConflict(outcome.record, unit.unitNumber, customer.id);
            showHint(fleetWarning ?? fleetCandidatesHint(outcome.record, customer.id));
            break;
          }
          case 'disabled':
          case 'notFound':
            showHint(null);
            /*
             * A genuine miss no longer blocks the operator — manual entry is required behaviour.
             * It used to set a validity error reading "No fleet file found".
             */
            break;
          case 'denied':
            fleetWarning = `Not permitted to check the fleet file for ${unit.unitNumber}`;
            showHint(fleetWarning);
            break;
          case 'unavailable':
          case 'timeout':
            /* the provider is down, not the unit missing. Say so, and let them continue. */
            inputElement?.setCustomValidity('');
            setAdornment(<WarningAdornment />);
            break;
          case 'invalidData':
          case 'invalidRequest':
            inputElement?.setCustomValidity('');
            setAdornment(<WarningAdornment />);
            break;
        }
        const allowOrdersResponse = await getAllowOrders(unit.unitNumber);
        /* if any allow orders, customer needs to be explicitly allowed */
        /* if no allow orders, customer needs not be explicitly stopped */
        if (allowOrdersResponse.length) {
          const allowOrder = allowOrdersResponse.find(ao => ao.customer.id === customer.id);
          if (!allowOrder) {
            inputElement?.setCustomValidity(`${unit.unitNumber} not allowed for ${customer.name ?? 'account'}!`);
            setAdornment(<WarningAdornment />);
          } else {
            inputElement?.setCustomValidity('');
            setAdornment(<AllowOrderAdornment {...allowOrder} />);
          }
        } else {
          const stopOrdersResponse = await getStopOrders(unit.unitNumber);
          const stopOrder = stopOrdersResponse.find(so => so.customer.id === customer.id);
          if (!stopOrder) {
            inputElement?.setCustomValidity('');
            setAdornment(
              fleetWarning ? (
                <WarningAdornment />
              ) : (
                <InputAdornment position="end">
                  <Done />
                </InputAdornment>
              )
            );
          } else {
            inputElement?.setCustomValidity(
              `${stopOrder.unitNumber} stopped for ${stopOrder.customer.name}! ${stopOrder.notes ?? ''}`
            );
            setAdornment(<WarningAdornment />);
          }
        }
      } else {
        setAdornment(null);
        /*
         * No account yet: with several candidates, say which accounts the fleet file allows, so the
         * operator can pick one through the search. Nothing is filled in on their behalf. The lookup
         * is the one the unit-number field already made, served from the shared cache.
         */
        if (unit.unitNumber && transaction.inbound) {
          const outcome = await getFleetFileUnit(unit.unitNumber, unit.equipmentType ?? undefined);
          showHint(outcome.kind === 'found' ? fleetCandidatesHint(outcome.record) : null);
        }
      }
      setCustomer(customer);
      // if (inputElement?.isSameNode(document.activeElement)) setTimeout(() => inputElement?.blur());
    },
    [unit.unitNumber, unit.equipmentType, transaction.inbound, getFleetFileUnit, setCustomer]
  );

  /* important - preserve timeout to avoid runaway calls on dependency changes */
  const timeout = useRef<NodeJS.Timeout>();
  const debounceValidation = useMemo(() => {
    return (...args: Parameters<typeof checkStopOrders>) => {
      clearTimeout(timeout.current);
      timeout.current = setTimeout(() => checkStopOrders(...args), 1000);
    };
  }, [checkStopOrders]);

  useEffect(() => {
    debounceValidation(unit.customer, inputRef.current);
  }, [unit.customer, debounceValidation]);

  return (
    <div className="account-async-search">
      <AsyncSearch<Customer>
        label="ACCOUNT"
        value={unit.customer}
        required={required}
        formatOption={({ name }) => name}
        formatValue={customer => customer?.name ?? ''}
        handleSelect={checkStopOrders}
        search={searchCustomers}
        adornment={adornment}
        disabled={!transaction.inbound && !unit.flatbed && !unit.ownerChassis}
        data-cy={props['data-cy']}
      />
      {!!candidatesHint && (
        <small className="fleet-candidates-hint" data-cy="fleet-candidates-hint">
          {candidatesHint}
        </small>
      )}
    </div>
  );
};

const AccountSearch: FC<AccountSearchProps> = props => {
  const { transaction } = useNewTransaction();
  return transaction.inbound ? <InboundAccountSearch {...props} /> : <LegacyAccountSearch {...props} />;
};

export default AccountSearch;
